import { useEffect, useState } from "react";
import { MsalProvider } from "@azure/msal-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { msalInstance } from "./auth/msalConfig.js";
import { AuthCallback } from "./auth/AuthCallback.js";
import { AuthGate } from "./auth/AuthGate.js";
import { StaffSurfaceGate } from "./auth/StaffSurfaceGate.js";
import { BrandedConsoleTitle } from "./auth/consoleTitle.js";
import { JobsScreen } from "./screens/JobsScreen.js";
import { JobDetailScreen } from "./screens/JobDetailScreen.js";
import { TenantsScreen } from "./screens/TenantsScreen.js";
import { TenantDetailScreen } from "./screens/TenantDetailScreen.js";
import { EmailDraftsScreen } from "./screens/EmailDraftsScreen.js";
import { EmailDraftDetailScreen } from "./screens/EmailDraftDetailScreen.js";
import { FailuresScreen } from "./screens/FailuresScreen.js";
import { WeeklyActivityScreen } from "./screens/WeeklyActivityScreen.js";
import { ForecastingScreen } from "./screens/ForecastingScreen.js";
import { ChatScreen } from "./screens/ChatScreen.js";
import { DocumentDetailScreen } from "./screens/DocumentDetailScreen.js";

// TanStack Query v5 — single shared client; defaults align with D-FETCH-01
// (4s polling, pause on hidden tab — overrides applied per-query).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000,           // brief stale window to dedupe rapid re-mounts
      refetchOnWindowFocus: true, // resume polling when tab focuses
      retry: (failureCount, _err) => {
        // No automatic retry on 401 — AuthGate handles re-auth.
        // No automatic retry on 4xx other than network/timeout — surface to UI.
        if (failureCount >= 1) return false;
        return true;
      },
    },
  },
});

/** Phase 15 D-AUTH-GATE-01 — tree shape:
 *    <MsalProvider><QueryClientProvider><BrowserRouter>
 *      Routes:
 *        /auth/callback   — AuthCallback (OUTSIDE AuthGate)
 *        / and everything else — AuthGate(<route>...)
 */
export function App() {
  // MSAL Browser v3 boot sequence — must run in this exact order before any
  // route renders, otherwise AuthGate races MsalProvider for the redirect
  // response and ends up double-prompting / stuck on "Checking your session...":
  //   1. initialize()              — required before any other MSAL API call
  //   2. handleRedirectPromise()   — consume any /auth/callback redirect code
  //                                  exactly once and populate the account cache
  //   3. setActiveAccount()        — pin the freshly-redeemed account so
  //                                  getActiveAccount() works synchronously
  //                                  in AuthGate's first useEffect
  // Only after all three complete do we render the router.
  const [msalReady, setMsalReady] = useState(false);
  useEffect(() => {
    (async () => {
      await msalInstance.initialize();
      const result = await msalInstance.handleRedirectPromise();
      if (result?.account) {
        msalInstance.setActiveAccount(result.account);
      } else {
        // No redirect response — pick up any account already in localStorage
        // from a prior session so refresh-after-login lands ready immediately.
        const all = msalInstance.getAllAccounts();
        if (all.length > 0 && all[0]) msalInstance.setActiveAccount(all[0]);
      }
      setMsalReady(true);
    })();
  }, []);

  if (!msalReady) return <div className="auth-loading">Loading...</div>;

  return (
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route
              path="/*"
              element={
                <AuthGate>
                  {/* Client-firm staff (tenant_config.staff_surface: portal) are sent to the
                      client portal before any console screen renders. */}
                  <StaffSurfaceGate>
                  {/* #3355 — a sole-tenant principal's tab wears the tenant's console_title. */}
                  <BrandedConsoleTitle />
                  <Routes>
                    <Route path="/" element={<Navigate to="/jobs" replace />} />
                    <Route path="/jobs" element={<JobsScreen />} />
                    <Route path="/jobs/:jobId" element={<JobDetailScreen />} />
                    <Route path="/tenants" element={<TenantsScreen />} />
                    <Route path="/tenants/:tenantId" element={<TenantDetailScreen />} />
                    <Route path="/email-drafts" element={<EmailDraftsScreen />} />
                    <Route path="/email-drafts/:draftId" element={<EmailDraftDetailScreen />} />
                    <Route path="/failures" element={<FailuresScreen />} />
                    <Route path="/reporting/weekly-activity" element={<WeeklyActivityScreen />} />
                    <Route path="/reporting/forecasting" element={<ForecastingScreen />} />
                    {/* Phase 408 (ADR-089) — the console chat panel. Inside AuthGate like
                        every other surface: the conversation store is principal-owned, so
                        the screen has nothing to show without a resolved operator. */}
                    <Route path="/chat" element={<ChatScreen />} />
                    {/* #1969 — one rendered document. The engine's render_document tool links
                        staff to https://<console host>/documents/<id>, so this path is a wire
                        contract, not a choice (test/polish/document-screen.test.ts). */}
                    <Route path="/documents/:documentId" element={<DocumentDetailScreen />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  </StaffSurfaceGate>
                </AuthGate>
              }
            />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </MsalProvider>
  );
}

function NotFound() {
  return <div style={{ padding: 24 }}>Page not found.</div>;
}
